import { ArrowBack } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Divider,
  Typography,
  useTheme,
} from '@mui/material';
import { useCallback, useContext, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import ArticlesApi from '../../../apis/ArticlesApi';
import LoadingCircle from '../../../components/LoadingCircle/LoadingCircle';
import SnackbarContext from '../../../contexts/SnackbarContext';
import { ArticleDto } from '../../../generated-types/articleDto';
import useApi from '../../../lib/useApi';
import { useHeaderControlContext } from '../Contexts/HeaderControlContext';
import SanitizeHtml from './SanitizeHtml';

/**
 * This view displays the actual article to the user within the article view
 */
const Article = () => {
  const { slug } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const articlesApi = useApi(ArticlesApi);
  const headerControl = useHeaderControlContext();
  const snackbarContext = useContext(SnackbarContext);
  const [article, setArticle] = useState<ArticleDto | null>(null);
  const [error, setError] = useState(false);
  const navigate = useNavigate();
  const theme = useTheme();

  /**
   * Set that an error occurred
   */
  const errorOccurred = useCallback(() => {
    snackbarContext.setAlert(
      <Alert severity="error" variant="filled">
        Error loading the article. Please try again. If this continues, try
        again later!
      </Alert>
    );
    snackbarContext.triggerSnackbar();
    setError(true);
  }, [snackbarContext]);

  /** Load the article */
  useEffect(() => {
    // We can't load if there's no slug
    if (slug == undefined) {
      errorOccurred();
      return;
    }

    // Get the article
    articlesApi
      .GetArticle(slug)
      .then((a) => {
        setArticle(a);

        // If the article is null, that means we did not find an article matching the slug
        if (a == null) {
          navigate('/not-found');
          return;
        }
      })
      .catch(errorOccurred)
      .finally(() => setIsLoading(false));
  }, [articlesApi, errorOccurred, navigate, slug]);

  /** Set the article title */
  useEffect(() => {
    if (article != null) {
      headerControl.setUseTitleH2(false);
      headerControl.setTitle(article.title);
      headerControl.setSubtitle(`By ${article.author}`);
    }

    // If we leave this, reset the title state
    return () => {
      headerControl.resetTitle();
    };
  }, [article, headerControl]);

  const getArticleDateStr = useCallback(() => {
    if (article == null) return '';
    return new Date(article.date).toLocaleDateString('en-us', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }, [article]);

  return (
    <Box>
      {!isLoading && !error && article != null && (
        <>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box display="flex" alignItems="center">
              <Button
                id="article-back-button"
                variant="outlined"
                startIcon={<ArrowBack />}
                component={Link}
                to="/articles"
                color="secondary"
                sx={{
                  marginRight: '1em',
                }}
              >
                Read More
              </Button>
            </Box>
            <Typography variant="h6">{getArticleDateStr()}</Typography>
          </Box>
          <article id="s3g-article">
            <Box
              paddingY="1em"
              minHeight="21em"
              sx={{
                '& a': {
                  color: theme.palette.text.primary,
                },
              }}
            >
              <Box
                sx={{
                  margin: {
                    xs: '0 auto 1em auto',
                    sm: '.5em 0 1em 1em',
                  },
                  display: 'flex',
                  alignContent: 'center',
                  justifyContent: 'center',
                  float: {
                    sm: 'right',
                  },
                }}
              >
                <img
                  src={article.imageUrl}
                  alt={article.altTag}
                  style={{
                    maxHeight: '18em',
                    maxWidth: '15em',
                    borderRadius: '.5em',
                    width: 'auto',
                    height: 'auto',
                  }}
                />
              </Box>
              <SanitizeHtml html={article.content} />
            </Box>
            <Divider />
            <Box
              component="footer"
              display="flex"
              justifyContent="space-between"
              flexWrap="wrap"
              paddingY="2em"
            >
              <address>Author: {article.author}</address>
              <Link
                to="/purchase-training"
                style={{ color: theme.palette.text.primary }}
              >
                Start training now
              </Link>
              <time dateTime={new Date(article.date).toISOString()}>
                Posted: {getArticleDateStr()}
              </time>
            </Box>
          </article>
        </>
      )}
      {!isLoading && error && (
        <Typography textAlign="center">
          An error occurred while loading the article. Please refresh your page
          to try again. If this continues, try again later!
        </Typography>
      )}
      <LoadingCircle loadingText="Loading article..." isLoading={isLoading} />
    </Box>
  );
};

export default Article;
